import { useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { isEmpty, isNil } from '../../utils/fp';
import {
  CHANGE_DATAFLOW,
  CHANGE_DATAQUERY,
  CHANGE_FREQUENCY_PERIOD,
  CHANGE_FREQUENCY_PERIOD_DATAQUERY,
  CHANGE_FREQUENCY_PERIOD_DATAQUERY_F,
  RESET_DATAFLOW,
} from '../../ducks/sdmx';
import { computeDataquery, updateDataquery } from '../../lib/sdmx/computors/dataquery';
import {
  getOnlyHasDataDimensions,
  getSelectedIdsIndexed,
  setSelectedDimensionsValues,
} from '../../lib/sdmx';
import { changeFrequency, getFrequencies, getSdmxPeriod } from '../../lib/sdmx/frequency';
import {
  getDataquery as getRouterDataquery,
  getLastNMode,
  getPeriod,
} from '../../selectors/router';
import { getDimensions, getFrequency, getFrequencyArtefact } from '../../selectors/sdmx';
import { END_PERIOD, LASTNOBSERVATIONS, LASTNPERIODS, START_PERIOD } from '../../utils/used-filter';
import { useVisNavigate } from '../useVisNavigate';

// [undefined, undefined] when the periods list is nil/empty, otherwise the list.
const orNoPeriod = periods =>
  isNil(periods) || isEmpty(periods) ? [undefined, undefined] : periods;

// Everything the vis page drops when the dataflow is left. Built per call: the
// payload is merged into the location state as is, so its empty containers must
// not be shared between two resets.
const dataflowReset = () => ({
  dataflow: null,
  filter: null,
  dataquery: null,
  hasDataAvailability: null,
  viewer: null,
  externalReference: null,
  isAvailabilityDisabled: false,
  // period: [undefined, undefined] is used to force no period, null means that default can be used
  period: null,
  lastNObservations: null,
  lastNMode: null,
  time: null,
  microdataConstraints: null,
  display: null,
  hierarchies: {},
  hCodes: {},
  defaultLayoutIds: null,
  constraintId: undefined,
  timePeriod: null,
  actualContentConstraints: [],
  customDataRequestSize: null,
  page: 0,
});

export const useSdmxNavigation = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useVisNavigate();

  return useMemo(() => {
    const changeLastNObservations = (lastNObservations, lastNMode, period = null) => {
      const state = store.getState();
      const frequency = getFrequency(state);
      const payload = { lastNMode, lastNObservations };
      if (lastNMode === LASTNPERIODS) {
        payload.period = null;
      }
      if (isNil(lastNObservations) || isEmpty(lastNObservations)) {
        payload.lastNMode = null;
        payload.period = [undefined, undefined];
      }
      if (!isNil(lastNObservations) && !isEmpty(lastNObservations) && isNil(lastNMode)) {
        payload.lastNMode = LASTNPERIODS;
        payload.period = null;
      }
      const currentLastNMode = getLastNMode(state);
      if (lastNMode !== currentLastNMode && lastNMode === LASTNOBSERVATIONS) {
        payload.period = orNoPeriod((period || []).map(getSdmxPeriod(frequency)));
      }
      navigate.push('/vis', payload);
    };

    return {
      changeLastNObservations,

      // replace is used when the dataflow is the only search result: the search
      // page it comes from must not stay in the history.
      changeDataflow: (dataflow, { replace = false, viewer } = {}) => {
        if (isNil(dataflow)) return;
        dispatch({ type: CHANGE_DATAFLOW });
        (replace ? navigate.replace : navigate.push)('/vis', { viewer, dataflow });
      },

      resetDataflow: () => {
        dispatch({ type: RESET_DATAFLOW });
        navigate.push('/', dataflowReset());
      },

      changeLastNObservationsDataquery: (
        lastNObservations,
        lastNMode,
        selection = {},
        frequency,
        period = null,
      ) => {
        const state = store.getState();
        const filterId = getFrequencyArtefact(state)?.id;
        const freq = getFrequency(state);
        const payload = { lastNMode, lastNObservations };
        if (lastNMode === LASTNPERIODS) {
          payload.period = null;
        }
        if (isNil(lastNObservations) || isEmpty(lastNObservations)) {
          payload.lastNMode = undefined;
          payload.period = [undefined, undefined];
        }
        if (!isNil(lastNObservations) && !isEmpty(lastNObservations) && isNil(lastNMode)) {
          payload.lastNMode = LASTNPERIODS;
          payload.period = null;
        }
        const currentLastNMode = getLastNMode(state);
        if (lastNMode !== currentLastNMode && lastNMode === LASTNOBSERVATIONS) {
          payload.period = orNoPeriod((period || []).map(getSdmxPeriod(frequency)));
        }
        let filters = selection;
        if (frequency && !isNil(frequency) && freq !== frequency) {
          filters = { ...selection, [filterId]: [frequency] };
        }
        const dataquery = updateDataquery(getDimensions(state), getRouterDataquery(state), {
          ...filters,
        });
        if (!isEmpty(filters)) {
          payload.dataquery = dataquery;
        }
        navigate.push('/vis', payload);
      },

      resetSpecialFilters: (_, ids) => {
        const id = Array.isArray(ids) ? ids[0] : ids;
        if (LASTNOBSERVATIONS === id) return changeLastNObservations();

        // special is start OR end (period)
        if (id === START_PERIOD || id === END_PERIOD) {
          const payloadPeriod = period => {
            if (id === START_PERIOD) return [undefined, period.at(-1)];
            if (id === END_PERIOD) return [period[0], undefined];
          };
          const period = payloadPeriod(getPeriod(store.getState()));
          dispatch({ type: CHANGE_FREQUENCY_PERIOD, payload: { period } });
          return navigate.push('/vis', { period });
        }

        // reset all special filters (period, lastNObservations)
        navigate.push('/vis', {
          period: [undefined, undefined],
          lastNObservations: null,
          lastNMode: null,
        });
      },

      changeDataquery: selection => {
        const state = store.getState();
        const currentDataquery = getRouterDataquery(state);
        const dataquery = updateDataquery(getDimensions(state), currentDataquery, {
          ...selection,
        });
        // An apply that lands on the query already in the url changes nothing,
        // and must say so: CHANGE_DATAQUERY nulls the time availability the data
        // url takes its startPeriod from, and nothing refetches it when the
        // query key it is cached under has not moved either. applyDataAvailability
        // below refuses the same no-op for the same reason.
        if (dataquery === currentDataquery) return;
        dispatch({ type: CHANGE_DATAQUERY, payload: { dataquery } });
        navigate.push('/vis', { dataquery });
      },

      applyDataAvailability: hasDataAvailability => {
        navigate.push('/vis', { hasDataAvailability });

        if (!hasDataAvailability) return;

        const state = store.getState();
        const currentDataquery = getRouterDataquery(state);
        const dimensions = getDimensions(state);
        const dimensionsWithSelectedValues = setSelectedDimensionsValues(
          currentDataquery,
          dimensions,
        );
        const frequencyArtefact = getFrequencyArtefact(state);
        const frequencyId = frequencyArtefact?.id;
        const availableFrequencies = getFrequencies(
          getOnlyHasDataDimensions([frequencyArtefact])[0],
        );
        const frequency = changeFrequency(getFrequency(state))(availableFrequencies);
        const selection = {
          ...getSelectedIdsIndexed(dimensionsWithSelectedValues),
          [frequencyId]: frequency,
        };
        const dataquery = computeDataquery(dimensions, selection);
        if (currentDataquery === dataquery) return;

        dispatch({ type: CHANGE_FREQUENCY_PERIOD, payload: { frequency } });
        dispatch({ type: CHANGE_DATAQUERY, payload: { dataquery } });
        navigate.push('/vis', { dataquery });
      },

      changeFrequencyPeriodDataquery: ({
        valueId,
        period,
        dataquery,
        lastNMode,
        lastNObservations,
      } = {}) => {
        const state = store.getState();
        const filterId = getFrequencyArtefact(state)?.id;
        const payload = { period: orNoPeriod(period.map(getSdmxPeriod(valueId))) };
        if (isNil(lastNMode)) {
          payload.lastNMode = undefined;
          payload.lastNObservations = undefined;
        }
        if (lastNMode === LASTNOBSERVATIONS) {
          payload.lastNMode = lastNMode;
          payload.lastNObservations = lastNObservations;
        }
        const newDataquery = updateDataquery(getDimensions(state), getRouterDataquery(state), {
          ...dataquery,
          [filterId]: [valueId],
        });
        const next = {
          ...payload,
          dataquery: newDataquery,
          period: lastNMode === LASTNPERIODS ? null : payload.period,
        };
        dispatch({ type: CHANGE_FREQUENCY_PERIOD_DATAQUERY, payload: next });
        navigate.push('/vis', next);
      },

      changeFrequencyPeriod: ({ valueId, period, lastNMode } = {}) => {
        const state = store.getState();
        const filterId = getFrequencyArtefact(state)?.id;
        const frequency = getFrequency(state);
        const payload = { period: orNoPeriod(period.map(getSdmxPeriod(valueId))) };
        if (isNil(lastNMode)) {
          payload.lastNMode = undefined;
          payload.lastNObservations = undefined;
        }

        if (!isNil(filterId) && frequency !== valueId) {
          const dataquery = updateDataquery(getDimensions(state), getRouterDataquery(state), {
            [filterId]: [valueId],
          });
          const next = {
            ...payload,
            dataquery,
            period: lastNMode === LASTNPERIODS ? null : payload.period,
          };
          dispatch({ type: CHANGE_FREQUENCY_PERIOD_DATAQUERY_F, payload: next });
          navigate.push('/vis', next);
          return;
        }

        dispatch({ type: CHANGE_FREQUENCY_PERIOD, payload, request: 'getData' });
        navigate.push('/vis', payload);
      },
    };
  }, [dispatch, navigate, store]);
};
